import React, { useEffect, useState } from 'react';
import { Megaphone, Send, Clock, Trash2, RotateCcw, X, Search, Loader2, Users, User as UserIcon, Filter, Check, Plus, Calendar, ExternalLink, Gift } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  adminBroadcastPreview,
  adminListBroadcasts,
  adminCreateBroadcast,
  adminSendBroadcastNow,
  adminCancelBroadcast,
  adminResendBroadcast,
  adminDeleteBroadcast,
  adminGetBroadcast,
  adminSearchUsers
} from '../../services/api';

const AUDIENCES = [
  { value: 'everyone', label: 'Everyone', icon: Users },
  { value: 'males', label: 'All males', icon: Users },
  { value: 'females', label: 'All females', icon: Users },
  { value: 'individual', label: 'Individual user', icon: UserIcon },
  { value: 'selected_users', label: 'Selected users', icon: Users },
  { value: 'custom_group', label: 'Custom group', icon: Filter },
];

const STATUS_STYLES = {
  sent: 'bg-green-100 text-green-800',
  sending: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-yellow-100 text-yellow-800',
  draft: 'bg-gray-100 text-gray-700',
  canceled: 'bg-red-100 text-red-800',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const BroadcastsTab = () => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminListBroadcasts({ limit: 100 });
      setBroadcasts(r.data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSendNow = async (id) => {
    if (!window.confirm('Send this broadcast now?')) return;
    try { await adminSendBroadcastNow(id); await load(); } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };
  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this scheduled broadcast?')) return;
    try { await adminCancelBroadcast(id); await load(); } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };
  const handleResend = async (id) => {
    if (!window.confirm('Resend this broadcast to the same audience?')) return;
    try { await adminResendBroadcast(id); await load(); } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this broadcast? All linked notifications will also be removed.')) return;
    try { await adminDeleteBroadcast(id); await load(); } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="broadcasts-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 text-lg" style={{ fontFamily: 'Outfit' }}>Broadcast Messaging</h3>
          <p className="text-sm text-gray-500">Reach users at scale — everyone, by gender, individual, or custom group</p>
        </div>
        <Button onClick={() => setComposerOpen(true)} className="bg-[#009E60] hover:bg-[#007A4B] rounded-full" data-testid="new-broadcast-btn">
          <Plus className="w-4 h-4 mr-2" /> New broadcast
        </Button>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#009E60] animate-spin" /></div>
        ) : broadcasts.length === 0 ? (
          <div className="text-center py-12" data-testid="broadcasts-empty">
            <Megaphone className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <div className="text-gray-500">No broadcasts yet — tap "New broadcast" to send one.</div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {broadcasts.map(b => (
              <div key={b.broadcast_id} className="p-4 flex items-start gap-3" data-testid={`broadcast-row-${b.broadcast_id}`}>
                <div className="w-9 h-9 rounded-xl bg-[#009E60]/10 flex items-center justify-center flex-shrink-0">
                  <Megaphone className="w-4 h-4 text-[#009E60]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 truncate">{b.subject}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLES[b.status] || 'bg-gray-100'}`}>{b.status.toUpperCase()}</span>
                    <span className="text-xs text-gray-500">{b.audience}{b.credit_amount ? ` · +${b.credit_amount}cr` : ''}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{b.body}</p>
                  <div className="text-xs text-gray-400 mt-1">
                    {b.status === 'sent' && `Sent ${fmtDate(b.sent_at)} · ${b.recipients_count || 0} recipients`}
                    {b.status === 'scheduled' && `Scheduled ${fmtDate(b.scheduled_at)}`}
                    {b.status === 'draft' && `Created ${fmtDate(b.created_at)}`}
                    {b.status === 'canceled' && `Canceled ${fmtDate(b.canceled_at)}`}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 items-end">
                  {(b.status === 'draft' || b.status === 'scheduled') && (
                    <Button size="sm" onClick={() => handleSendNow(b.broadcast_id)} className="bg-[#009E60] hover:bg-[#007A4B] rounded-full h-8 text-xs" data-testid={`send-now-${b.broadcast_id}`}>
                      <Send className="w-3 h-3 mr-1" /> Send now
                    </Button>
                  )}
                  {b.status === 'scheduled' && (
                    <Button size="sm" variant="outline" onClick={() => handleCancel(b.broadcast_id)} className="rounded-full h-8 text-xs" data-testid={`cancel-${b.broadcast_id}`}>
                      Cancel
                    </Button>
                  )}
                  {b.status === 'sent' && (
                    <Button size="sm" variant="outline" onClick={() => handleResend(b.broadcast_id)} className="rounded-full h-8 text-xs" data-testid={`resend-${b.broadcast_id}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Resend
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setDetail(b.broadcast_id)} className="rounded-full h-8 text-xs" data-testid={`detail-${b.broadcast_id}`}>
                    Details
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(b.broadcast_id)} className="text-red-500 hover:bg-red-50 rounded-full h-8 text-xs" data-testid={`delete-${b.broadcast_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {composerOpen && (
        <BroadcastComposer
          onClose={() => setComposerOpen(false)}
          onSaved={() => { setComposerOpen(false); load(); }}
        />
      )}
      {detail && (
        <BroadcastDetailModal broadcastId={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
};

// ============ COMPOSER ============

const BroadcastComposer = ({ onClose, onSaved }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('everyone');
  const [targetUsers, setTargetUsers] = useState([]); // [{user_id, name, email}]
  const [groupFilters, setGroupFilters] = useState({});
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [creditAmount, setCreditAmount] = useState(0);
  const [scheduledAt, setScheduledAt] = useState('');
  const [count, setCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userLoading, setUserLoading] = useState(false);

  const canPreview = subject.trim() && body.trim() && (
    (audience === 'individual' && targetUsers.length === 1) ||
    (audience === 'selected_users' && targetUsers.length >= 1) ||
    (audience === 'custom_group') ||
    ['everyone', 'males', 'females'].includes(audience)
  );

  const buildPayload = () => ({
    subject: subject.trim(),
    body: body.trim(),
    audience,
    target_user_ids: (audience === 'individual' || audience === 'selected_users') ? targetUsers.map(u => u.user_id) : [],
    group_filters: audience === 'custom_group' ? groupFilters : null,
    image_url: imageUrl.trim() || null,
    link_url: linkUrl.trim() || null,
    link_label: linkLabel.trim() || null,
    credit_amount: parseInt(creditAmount) || 0,
    scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null
  });

  const handlePreview = async () => {
    setError('');
    setPreviewLoading(true);
    try {
      const r = await adminBroadcastPreview(buildPayload());
      setCount(r.data.count);
      setShowConfirm(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const created = await adminCreateBroadcast(buildPayload());
      if (!scheduledAt) {
        await adminSendBroadcastNow(created.data.broadcast_id);
      }
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  // User search
  useEffect(() => {
    if (!(audience === 'individual' || audience === 'selected_users')) { setUserResults([]); return; }
    if (userSearch.trim().length < 2) { setUserResults([]); return; }
    let cancelled = false;
    setUserLoading(true);
    const timer = setTimeout(() => {
      adminSearchUsers(userSearch.trim(), { limit: 12 }).then(r => {
        if (!cancelled) setUserResults(r.data.users || []);
      }).finally(() => { if (!cancelled) setUserLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [userSearch, audience]);

  const pickUser = (u) => {
    if (audience === 'individual') {
      setTargetUsers([{ user_id: u.user_id, name: u.name, email: u.email }]);
    } else {
      setTargetUsers(prev => prev.find(x => x.user_id === u.user_id) ? prev : [...prev, { user_id: u.user_id, name: u.name, email: u.email }]);
    }
    setUserSearch('');
    setUserResults([]);
  };
  const removeUser = (uid) => setTargetUsers(prev => prev.filter(u => u.user_id !== uid));

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="broadcast-composer">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#009E60]" />
            <h3 className="font-bold text-gray-900">New broadcast</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100" data-testid="composer-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Audience */}
          <div>
            <Label className="text-sm font-semibold text-gray-800">Send to</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {AUDIENCES.map(a => (
                <button
                  key={a.value}
                  onClick={() => { setAudience(a.value); setTargetUsers([]); }}
                  className={`px-3 py-2 rounded-xl border-2 text-sm flex items-center gap-2 transition-all ${audience === a.value ? 'border-[#009E60] bg-[#009E60]/5 text-[#007A4B]' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`audience-${a.value}`}
                >
                  <a.icon className="w-4 h-4" />
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Individual / Selected users picker */}
          {(audience === 'individual' || audience === 'selected_users') && (
            <div>
              <Label className="text-sm font-semibold text-gray-800">
                {audience === 'individual' ? 'Choose one user' : 'Choose users (multiple)'}
              </Label>
              {targetUsers.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {targetUsers.map(u => (
                    <span key={u.user_id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#009E60]/10 text-[#007A4B] text-xs font-medium" data-testid={`picked-user-${u.user_id}`}>
                      {u.name || u.email}
                      <button onClick={() => removeUser(u.user_id)} className="hover:bg-white/40 rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search by name or email"
                  className="pl-9 rounded-xl"
                  data-testid="user-search-input" />
              </div>
              {userLoading && <div className="text-xs text-gray-500 mt-1">Searching…</div>}
              {userResults.length > 0 && (
                <div className="mt-2 border border-gray-100 rounded-xl divide-y divide-gray-100 max-h-52 overflow-y-auto">
                  {userResults.map(u => (
                    <button
                      key={u.user_id}
                      onClick={() => pickUser(u)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                      data-testid={`user-result-${u.user_id}`}
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">{u.name || 'No name'}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                      <Plus className="w-4 h-4 text-[#009E60]" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Custom group filters */}
          {audience === 'custom_group' && (
            <div className="bg-gray-50 rounded-xl p-3 space-y-2" data-testid="custom-group-filters">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Country" value={groupFilters.country || ''} onChange={(e) => setGroupFilters({ ...groupFilters, country: e.target.value })} data-testid="filter-country" />
                <Input placeholder="City" value={groupFilters.city || ''} onChange={(e) => setGroupFilters({ ...groupFilters, city: e.target.value })} data-testid="filter-city" />
                <Input placeholder="Region" value={groupFilters.region || ''} onChange={(e) => setGroupFilters({ ...groupFilters, region: e.target.value })} data-testid="filter-region" />
                <Input placeholder="Ethnicity" value={groupFilters.ethnicity || ''} onChange={(e) => setGroupFilters({ ...groupFilters, ethnicity: e.target.value })} data-testid="filter-ethnicity" />
                <Input placeholder="Gender (man/woman)" value={groupFilters.gender || ''} onChange={(e) => setGroupFilters({ ...groupFilters, gender: e.target.value })} data-testid="filter-gender" />
                <Input placeholder="Religion" value={groupFilters.religion || ''} onChange={(e) => setGroupFilters({ ...groupFilters, religion: e.target.value })} data-testid="filter-religion" />
                <Input type="number" placeholder="Min age" value={groupFilters.min_age || ''} onChange={(e) => setGroupFilters({ ...groupFilters, min_age: e.target.value })} data-testid="filter-min-age" />
                <Input type="number" placeholder="Max age" value={groupFilters.max_age || ''} onChange={(e) => setGroupFilters({ ...groupFilters, max_age: e.target.value })} data-testid="filter-max-age" />
                <Input type="number" placeholder="Signed up within N days" value={groupFilters.new_within_days || ''} onChange={(e) => setGroupFilters({ ...groupFilters, new_within_days: e.target.value })} data-testid="filter-new-within" />
                <Input type="number" placeholder="Active within N days" value={groupFilters.active_within_days || ''} onChange={(e) => setGroupFilters({ ...groupFilters, active_within_days: e.target.value })} data-testid="filter-active-within" />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={groupFilters.is_premium === true} onChange={(e) => setGroupFilters({ ...groupFilters, is_premium: e.target.checked ? true : null })} data-testid="filter-premium" />
                Premium subscribers only
              </label>
            </div>
          )}

          {/* Subject + Body */}
          <div>
            <Label className="text-sm font-semibold text-gray-800">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value.slice(0, 120))} className="mt-1 rounded-xl" placeholder="Big news for GuyanaVibes!" data-testid="broadcast-subject" />
            <p className="text-xs text-gray-400 text-right mt-0.5">{subject.length}/120</p>
          </div>
          <div>
            <Label className="text-sm font-semibold text-gray-800">Message</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value.slice(0, 2000))} className="mt-1 min-h-[120px] rounded-xl resize-none" placeholder="Type your message here. It will appear in each recipient's Notifications." data-testid="broadcast-body" />
            <p className="text-xs text-gray-400 text-right mt-0.5">{body.length}/2000</p>
          </div>

          {/* Optional image + link */}
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Image URL (optional)" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="rounded-xl" data-testid="broadcast-image-url" />
            <Input placeholder="Link URL (optional)" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className="rounded-xl" data-testid="broadcast-link-url" />
            <Input placeholder="Link label (e.g., Learn more)" value={linkLabel} onChange={(e) => setLinkLabel(e.target.value.slice(0, 60))} className="rounded-xl" data-testid="broadcast-link-label" />
            <div className="flex items-center gap-2 bg-yellow-50 rounded-xl px-3 py-2">
              <Gift className="w-4 h-4 text-yellow-600" />
              <Input type="number" min="0" placeholder="Free credits" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} className="rounded-lg border-0 bg-transparent" data-testid="broadcast-credit-amount" />
            </div>
          </div>

          {/* Schedule */}
          <div>
            <Label className="text-sm font-semibold text-gray-800 flex items-center gap-1"><Calendar className="w-4 h-4" /> Schedule for later (optional)</Label>
            <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="mt-1 rounded-xl" data-testid="broadcast-scheduled-at" />
            <p className="text-xs text-gray-500 mt-1">Leave blank to send immediately.</p>
          </div>

          {error && <div className="bg-red-50 text-red-600 rounded-xl p-3 text-sm">{error}</div>}
        </div>

        <div className="border-t border-gray-100 p-4 flex items-center gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1" data-testid="composer-cancel">Cancel</Button>
          <Button onClick={handlePreview} disabled={!canPreview || previewLoading} className="flex-1 bg-[#009E60] hover:bg-[#007A4B] rounded-full" data-testid="composer-preview">
            {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> {scheduledAt ? 'Preview & schedule' : 'Preview & send'}</>}
          </Button>
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-3" onClick={() => setShowConfirm(false)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()} data-testid="confirm-dialog">
            <div className="w-14 h-14 rounded-full bg-[#009E60]/10 flex items-center justify-center mx-auto mb-3">
              <Megaphone className="w-6 h-6 text-[#009E60]" />
            </div>
            <h4 className="font-bold text-gray-900 mb-1">Send this broadcast?</h4>
            <p className="text-sm text-gray-600 mb-4">
              You are about to send this message to <span className="font-bold text-[#009E60]">{count?.toLocaleString?.() || count}</span> user{count === 1 ? '' : 's'}.
              {parseInt(creditAmount) > 0 && <> Each will receive <span className="font-bold text-[#009E60]">{creditAmount} free credit{parseInt(creditAmount) === 1 ? '' : 's'}</span>.</>}
              {scheduledAt && <> It will be sent {new Date(scheduledAt).toLocaleString()}.</>}
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowConfirm(false)} className="flex-1" data-testid="confirm-cancel">Back</Button>
              <Button onClick={async () => { await handleSend(); setShowConfirm(false); }} disabled={sending} className="flex-1 bg-[#009E60] hover:bg-[#007A4B] rounded-full" data-testid="confirm-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (scheduledAt ? 'Schedule' : 'Send now')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ DETAIL MODAL ============

const BroadcastDetailModal = ({ broadcastId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    adminGetBroadcast(broadcastId).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [broadcastId]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-3" onClick={onClose} data-testid="broadcast-detail">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Broadcast details</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#009E60]" /></div>
        ) : !data ? (
          <div className="p-6 text-center text-gray-500">Not found</div>
        ) : (
          <div className="p-4 overflow-y-auto space-y-3">
            <div><span className="text-xs text-gray-500">Subject</span><div className="font-semibold text-gray-900">{data.subject}</div></div>
            <div><span className="text-xs text-gray-500">Body</span><div className="text-sm text-gray-700 whitespace-pre-wrap">{data.body}</div></div>
            {data.image_url && (
              <div><span className="text-xs text-gray-500">Image</span><img src={data.image_url} alt="broadcast" className="mt-1 max-h-40 rounded-xl object-cover" /></div>
            )}
            {data.link_url && (
              <div><span className="text-xs text-gray-500">Link</span><a href={data.link_url} target="_blank" rel="noopener noreferrer" className="text-[#009E60] hover:underline text-sm flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> {data.link_label || data.link_url}</a></div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-gray-100">
              <Stat label="Status" value={data.status} />
              <Stat label="Audience" value={data.audience} />
              <Stat label="Recipients" value={data.recipients_count || 0} />
              <Stat label="Read" value={`${data.read_count ?? 0}${data.read_rate !== undefined ? ` (${data.read_rate}%)` : ''}`} />
              {data.credit_amount > 0 && <Stat label="Credits per user" value={data.credit_amount} />}
              {data.credit_amount > 0 && <Stat label="Credits granted" value={data.credits_granted_count || 0} />}
              <Stat label="Created" value={fmtDate(data.created_at)} />
              {data.sent_at && <Stat label="Sent" value={fmtDate(data.sent_at)} />}
              {data.scheduled_at && <Stat label="Scheduled" value={fmtDate(data.scheduled_at)} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div><span className="text-xs text-gray-500 block">{label}</span><span className="font-medium text-gray-900">{value}</span></div>
);

export default BroadcastsTab;
